// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Modal as PingModal } from '../../components/Modal';
import { createTheme } from '../../design-system/theme';
import { notesStrings } from './strings';
import type { NoteFolderId } from './types';

export interface NewNoteDraft {
  readonly title: string;
  readonly body: string;
  readonly folderId: NoteFolderId | null;
}

export interface NewNoteModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (draft: NewNoteDraft) => void;
  readonly initialFolderId?: NoteFolderId | null;
}

export const NewNoteModal: React.FC<NewNoteModalProps> = ({
  visible,
  onClose,
  onSubmit,
  initialFolderId,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const [title, setTitle] = useState('');

  const handleSubmit = useCallback(() => {
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      return;
    }
    onSubmit({ title: trimmed, body: '', folderId: initialFolderId ?? null });
    setTitle('');
    onClose();
  }, [title, initialFolderId, onSubmit, onClose]);

  const handleCancel = useCallback(() => {
    setTitle('');
    onClose();
  }, [onClose]);

  return (
    <PingModal
      visible={visible}
      onClose={handleCancel}
      accessibilityLabel={notesStrings.newNoteTitle}
    >
      <View>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {notesStrings.newNoteTitle}
        </Text>
        <Text style={[styles.label, { color: theme.colors.text.secondary }]}>
          {notesStrings.noteTitleLabel}
        </Text>
        <TextInput
          value={title}
          onChangeText={setTitle}
          placeholder={notesStrings.noteTitlePlaceholder}
          placeholderTextColor={theme.colors.text.tertiary}
          accessibilityLabel={notesStrings.noteTitleLabel}
          autoFocus
          style={[
            styles.input,
            {
              color: theme.colors.text.primary,
              borderColor: theme.colors.border.primary,
              backgroundColor: theme.colors.surface.secondary,
            },
          ]}
        />
        <View style={styles.actions}>
          <Pressable
            onPress={handleCancel}
            accessibilityRole="button"
            accessibilityLabel={notesStrings.cancelCta}
            style={({ pressed }) => [styles.actionButton, pressed && { opacity: 0.7 }]}
          >
            <Text style={[styles.cancelText, { color: theme.colors.text.secondary }]}>
              {notesStrings.cancelCta}
            </Text>
          </Pressable>
          <Pressable
            onPress={handleSubmit}
            accessibilityRole="button"
            accessibilityLabel={notesStrings.saveCta}
            accessibilityState={{ disabled: title.trim().length === 0 }}
            disabled={title.trim().length === 0}
            style={({ pressed }) => [
              styles.actionButton,
              styles.primaryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
              title.trim().length === 0 && { opacity: 0.4 },
            ]}
          >
            <Text style={styles.primaryText}>{notesStrings.saveCta}</Text>
          </Pressable>
        </View>
      </View>
    </PingModal>
  );
};

const styles = StyleSheet.create({
  title: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 12,
  } satisfies TextStyle,
  label: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
    marginTop: 12,
    textTransform: 'uppercase',
  } satisfies TextStyle,
  input: {
    fontSize: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  } satisfies TextStyle,
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
  } satisfies ViewStyle,
  actionButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    marginLeft: 8,
  } satisfies ViewStyle,
  primaryButton: {
    minWidth: 80,
    alignItems: 'center',
  } satisfies ViewStyle,
  cancelText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  primaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
